import { createContext, useContext, useEffect, useState } from 'react'
import { usersRoutes, rolesRoutes } from '../routes'
import { useAuth, useUser, useSitesContext } from '.'
import { ROLES } from '../config/Constants'

export const UsersContext = createContext()

export const UsersProvider = props => {
    const { isAuthenticated, userData } = useAuth()
    const { admin, isSuperAdmin, isSapExecutive, isShiftManager, isChiefMachinery, isOperator, isOperatorInspector, isUniversalChief, isUniversalInspector } = useUser()
    const { siteSelected } = useSitesContext()
    const [users, setUsers] = useState([])
    const [roles, setRoles] = useState([])
    const [usersFilteredBySite, setUsersFilteredBySite] = useState([])

    // Categorized User Lists
    const [inspectors, setInspectors] = useState([])
    const [maitenances, setMaintenances] = useState([])
    const [operators, setOperators] = useState([])
    const [chiefs, setChiefs] = useState([])
    const [admins, setAdmins] = useState([])
    const [usersMap, setUsersMap] = useState({})

    const [loadingUsers, setLoadingUsers] = useState(true)

    useEffect(() => {
        if (users.length > 0) {
            const map = {}
            users.forEach(u => {
                map[u._id] = u
            })
            setUsersMap(map)
        }
    }, [users])

    useEffect(() => {
        if (isAuthenticated && (admin || isSuperAdmin || isSapExecutive || isShiftManager || isChiefMachinery || isOperatorInspector)) {
            getUsers()
        }
    }, [isAuthenticated, admin, isSuperAdmin, isSapExecutive, isShiftManager, isChiefMachinery, isOperatorInspector])

    useEffect(() => {
        if (isAuthenticated && (admin || isSuperAdmin)) {
            getRoles()
        }
    }, [isAuthenticated, admin, isSuperAdmin])

    useEffect(() => {
        if (!admin && (users.length > 0) && siteSelected) {
            console.log('Usuarios descargados: ', users.length)
            const usersCache = users.filter(user => user.obras && user.obras.some(obra => {
                const obraId = (typeof obra === 'object') ? (obra._id || obra.id) : obra
                return obraId === siteSelected._id
            }))
            console.log('Usuarios filtrados: ', usersCache.length)
            setUsersFilteredBySite(usersCache)
        }
    }, [users, siteSelected])

    useEffect(() => {
        const inspectorsCache = []
        const maintenancesCache = []
        const operatorsCache = []
        const chiefsCache = []
        const adminsCache = []

        const sourceUsers = (admin || !siteSelected || siteSelected._id === 'all') ? users : usersFilteredBySite

        if (sourceUsers.length > 0) {
            sourceUsers.forEach(user => {
                if (user.role && typeof user.role === 'object') {
                    const roleName = user.role.dbName

                    // Inspectors
                    if (roleName === ROLES.INSPECTION_WORKER || roleName === ROLES.OPERATOR_INSPECTOR_UNIVERSAL || roleName === ROLES.OPERATOR_INSPECTOR) {
                        inspectorsCache.push(user)
                        operatorsCache.push(user)
                    }

                    // Maintenance
                    if (roleName === ROLES.MAINTENANCE_OPERATOR) {
                        maintenancesCache.push(user)
                        operatorsCache.push(user)
                    }

                    // Generic Operators
                    if (roleName === ROLES.OPERATOR) {
                        operatorsCache.push(user)
                    }

                    // Chiefs (Shift Manager & Chief Machinery)
                    if (
                        roleName === ROLES.SHIFT_MANAGER ||
                        roleName === ROLES.CHIEF_MACHINERY ||
                        roleName === ROLES.SUPERVISOR ||
                        roleName === ROLES.OPERATOR_MACHINERY_UNIVERSAL
                    ) {
                        chiefsCache.push(user)
                    }

                    // Admins
                    if (
                        roleName === ROLES.ADMIN ||
                        roleName === ROLES.SUPER_ADMIN ||
                        roleName === ROLES.SAP_EXECUTIVE ||
                        roleName === ROLES.OPERATOR_INSPECTOR
                    ) {
                        adminsCache.push(user)
                    }
                }
            })

            setInspectors(inspectorsCache)
            setMaintenances(maintenancesCache)
            setOperators(operatorsCache)
            setChiefs(chiefsCache)
            setAdmins(adminsCache)
        }
    }, [usersFilteredBySite, admin, users, siteSelected])

    const getRoles = async () => {
        try {
            const response = await rolesRoutes.getRoles()
            setRoles(response.data)
        } catch (error) {
            console.error('Error fetching roles:', error)
        }
    }

    const getUsers = async () => {
        setLoadingUsers(true)
        const response = await usersRoutes.getAllUsers() // No parameters needed
        setUsers(response.data)
        setLoadingUsers(false)
    }

    const createUser = async (newUserData, password) => {
        try {
            const payload = {
                ...newUserData,
                enabled: true // Always enable users on creation from admin flow
            }
            const response = await usersRoutes.createUser(payload, password)
            if (response.status === 201 || response.status === 200) {
                // Refresh the users list after successful creation
                await getUsers()
                return { success: true, data: response.data }
            }
            return { success: false, error: 'Respuesta no esperada del servidor.' }
        } catch (error) {
            console.error('Error creating user in context:', error.response ? error.response.data : error)
            const serverMessage = error.response?.data?.message
            const formattedError = Array.isArray(serverMessage)
                ? serverMessage.join(', ')
                : (typeof serverMessage === 'string' ? serverMessage : (error.message || 'Error desconocido al crear usuario.'))
            const statusCode = error.response?.status || error.response?.data?.statusCode || 'Error'
            return { success: false, error: formattedError, statusCode, rawError: error.response?.data }
        }
    }

    const provider = {
        users,
        roles,
        setUsers,
        getUsers,
        createUser,
        usersFilteredBySite,
        inspectors,
        maitenances,
        operators,
        chiefs,
        admins,
        usersMap,
        loadingUsers
    }

    return (
        <UsersContext.Provider value={provider} {...props} />
    )
}

export const useUsersContext = () => useContext(UsersContext)
